import React from 'react';
import {
  Box, Button, IconButton, Stack, Typography,
} from '@mui/material';
import { Trans } from '@gamiphy/react-i18n';
import { observer } from 'mobx-react';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import profits from '@gamiphy/loyalty-station-desktop/components/cashback/assets/profits.svg';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { WelcomeGiftDto } from '@coretava/api/ecommerce-service';
import { formatCashbackPromotionValue, formatWelcomeGiftValue } from '@gamiphy/loyalty-station-desktop/components/cashback';
import Close from '@mui/icons-material/Close';
import { CashbackPopupsStore } from '@gamiphy/loyalty-station-desktop/store/cashbackPopups';
import Verified from '../loggedIn/Verified';

const WelcomeGift: React.FC<{
  onSignupClick: () => void,
  welcomeGift: WelcomeGiftDto,
}> = ({
  onSignupClick,
  welcomeGift,
}) => {
  const { title, description } = React.useMemo<{ title: React.ReactNode, description: React.ReactNode }>(() => {
    if (AppStore.app.agent === 'wake') {
      return {
        title: <Trans
          i18nKey="cashbackPopup.discountAwaits"
          defaults="Congrats!<br />{{discount}} discount awaits you!"
          values={{
            discount: formatWelcomeGiftValue(welcomeGift),
          }}
        />,
        description: <Trans
          i18nKey="cashbackPopup.becomeMember.welcomeGift.predefinedPromotions"
          defaults="Join now to receive {{welcomeGiftValue}} off your first purchase over {{minPurchase, currency}}, and  up to {{promotionCashback}} cashback on your entire order."
          values={{
            welcomeGiftValue: formatWelcomeGiftValue(welcomeGift),
            minPurchase: welcomeGift?.minPurchaseAmount,
            promotionCashback: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
          }}
        />,
      };
    }

    return {
      title: <Trans
        i18nKey="cashbackPopup.discountAwaits"
        defaults="Congrats!<br />{{discount}} discount awaits you!"
        values={{
          discount: formatWelcomeGiftValue(welcomeGift),
        }}
      />,
      description: AppCashbackStore.defaultAppCashback?.enabled
        ? (
          <Trans
            i18nKey="cashbackPopup.becomeMember.welcomeGift"
            defaults="Join now to receive {{welcomeGiftValue}} off your first purchase over {{minPurchase, currency}}, and enjoy {{cashbackPercentage}}% cashback on your entire order."
            values={{
              welcomeGiftValue: formatWelcomeGiftValue(welcomeGift),
              minPurchase: welcomeGift?.minPurchaseAmount,
              cashbackPercentage: AppCashbackStore.defaultAppCashback?.percentage,
            }}
          />
        )
        : (
          <Trans
            i18nKey="cashbackPopup.becomeMember.welcomeGiftWithoutCashback"
            defaults="Join now to receive {{welcomeGiftValue}} off your first purchase over {{minPurchase, currency}}."
            values={{
              welcomeGiftValue: formatWelcomeGiftValue(welcomeGift),
              minPurchase: welcomeGift?.minPurchaseAmount,
            }}
          />
        ),
    };
  }, [AppCashbackStore.defaultAppCashback,
    welcomeGift,
    AppStore.app.agent,
    AppCashbackStore.highestPromotionAppCashback,
  ]);

  if (UserStore.user?.phone) return <Verified />;

  return (
    <Box p={2} maxWidth={350}>
      <Box pb="1.6rem" display="flex" justifyContent="flex-end">
        <IconButton sx={{ ml: 'auto', p: 0 }} disableRipple onClick={() => CashbackPopupsStore.closeCashbackPopup()}>
          <Close fontSize="large" />
        </IconButton>
      </Box>
      <Stack alignItems="center" spacing={1} textAlign="center">
        <Box component="img" src={ThemeStore.theme.popupsConfig?.welcomeGift?.image ?? profits} width={96} />
        <Typography fontSize={20} fontWeight={700}>
          {title}
        </Typography>
        <Typography
          fontSize={14}
          fontWeight={500}
          sx={{
            opacity: 0.5,
            mb: 1,
          }}
        >
          {description}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={onSignupClick}
          sx={{
            fontSize: 16,
          }}
        >
          <Trans
            i18nKey="shared.signUp"
            defaults="Sign Up"
          />
        </Button>
      </Stack>
    </Box>
  );
};

export default observer(WelcomeGift);
